import { useMemo } from "react";
import { Link } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import { cn } from "cn";
import type { Pitch } from "../shared/types";

type ManagerMonthStatsProps = {
    pitches: readonly Pick<Pitch, "status">[];
    loading?: boolean;
    actionLabel: string;
    actionTo: string;
};

/** Four value/label pulses and a link bar in the month-stats shell. */
export function ManagerMonthStatsSkeleton() {
    return (
        <div className="flex flex-wrap items-center justify-between gap-4 bg-muted px-4 py-4">
            {/* Counts sit in a row with a rule between each pair. */}
            <div className="flex items-stretch gap-8">
                <StatSkeleton valueClassName="w-12" labelClassName="w-44" />
                <div className="w-px bg-gray-300" aria-hidden />
                <StatSkeleton valueClassName="w-10" labelClassName="w-20" />
                <div className="w-px bg-gray-300" aria-hidden />
                <StatSkeleton valueClassName="w-10" labelClassName="w-20" />
                <div className="w-px bg-gray-300" aria-hidden />
                <StatSkeleton valueClassName="w-10" labelClassName="w-24" />
            </div>
            <Skeleton className="h-6 w-32" />
        </div>
    );
}

/** This-month pitch counts shared by the opportunities index and My Pitches. */
export default function ManagerMonthStats({ pitches, loading, actionLabel, actionTo }: ManagerMonthStatsProps) {
    const stats = useMemo(() => monthPitchStats(pitches), [pitches]);
    const phase = useSkeletonPhases(Boolean(loading));

    if (phase === "showSkeleton") {
        return <ManagerMonthStatsSkeleton />;
    }
    if (phase === "showNothing") {
        return null;
    }

    return (
        <div className="flex flex-wrap items-center justify-between gap-4 bg-muted px-4 py-4">
            {/* Counts sit in a row with a rule between each pair. */}
            <div className="flex items-stretch gap-8">
                <Stat value={stats.total} label="My pitches this month" />
                <div className="w-px bg-gray-300" aria-hidden />
                <Stat value={stats.approved} label="Approved" valueClassName="text-success-500" />
                <div className="w-px bg-gray-300" aria-hidden />
                <Stat value={stats.rejected} label="Rejected" valueClassName="text-destructive-500" />
                <div className="w-px bg-gray-300" aria-hidden />
                <Stat value={stats.withdrawn} label="Withdrawn" />
            </div>
            <Button variant="link" render={<Link to={actionTo} />}>
                {actionLabel}
            </Button>
        </div>
    );
}

/** Counts the pitches already scoped to this month by the query. Status counts are subsets of that total. */
function monthPitchStats(pitches: readonly Pick<Pitch, "status">[]) {
    const stats = { total: 0, approved: 0, rejected: 0, withdrawn: 0 };
    for (const pitch of pitches) {
        stats.total += 1;
        if (pitch.status === "approved") {
            stats.approved += 1;
        }
        if (pitch.status === "rejected") {
            stats.rejected += 1;
        }
        if (pitch.status === "withdrawn") {
            stats.withdrawn += 1;
        }
    }
    return stats;
}

function Stat({ value, label, valueClassName }: { value: number; label: string; valueClassName?: string }) {
    return (
        <div>
            <div className={cn("text-2xl/none font-bold", valueClassName)}>{value}</div>
            <div className="text-base/6 uppercase text-muted-foreground">{label}</div>
        </div>
    );
}

function StatSkeleton({ valueClassName, labelClassName }: { valueClassName: string; labelClassName: string }) {
    return (
        <div>
            <Skeleton className={cn("mb-1 h-8", valueClassName)} />
            <Skeleton className={cn("h-6", labelClassName)} />
        </div>
    );
}
